import { IngredientsContext } from "@/app/clientLayout";
import DialogComponent from "@/components/Dialog";
import DragNDrop from "@/components/DragNDrop";
import ErrorMessage from "@/components/ErrorMessage";
import GetImageURL from "@/components/GetImageURL";
import { CurrentUser, UserContext } from "@/components/Page";
import { FolderSelectInput } from "@/components/RecipesPage/util";
import TextEditor from "@/components/TextEditor";
import { Input } from "@/components/styles/Layout";
import Tooltip from "@/components/styles/Tooltip";
import {
  ShapedClass,
  dot,
  searchClasses,
  shapeClass,
  shapeClassLinear,
} from "@/lib/class-utilities";
import { combineUnits, searchForUnit } from "@/lib/measurement";
import useDebounce from "@/lib/useDebounce";
import {
  isAdmin,
  isAssistant,
  isStudent,
  isTeacher,
  searchTags,
} from "@/lib/util";
import {
  AncillariesComponent,
  Recipe_MadeInGroups,
} from "@/queries/autogenerate/graphql";
import {
  ArrowPathIcon,
  CameraIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import cuid from "cuid";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { SharedRecipeInfo, SimilarRecipes } from "../CreateRecipeManually";
import { PartialRecipe } from "./FinalisePartialRecipe";
import { IngredientSection } from "./IngredientSection";

interface Props {
  user: CurrentUser;
  recipe: PartialRecipe;
  recipes: PartialRecipe[];
  setRecipes: Dispatch<SetStateAction<PartialRecipe[]>>;
}

export const IndividualRecipe: React.FC<Props> = ({
  user,
  recipe: {
    uuid,
    name,
    folder,
    source,
    public: isPublic,
    image,
    servings,
    madeInGroups,
    classId,
    tags: ingredientTags,
    ingredientSections,
    method,
    recipeInformation,
  },
  recipes,
  setRecipes,
}) => {
  const [visibleDialog, setVisibleDialog] = useState<string | null>(null);

  const { ingredients: defaultIngredients } = useContext(IngredientsContext);

  const setIngredientSections: (
    id: string,
    newSections: PartialRecipe["ingredientSections"]
  ) => void = (id, newSections) => {
    setRecipes((recipes) =>
      recipes.map((r) => {
        if (r.uuid != id) return r;

        return {
          ...r,
          ingredientSections: newSections,
        };
      })
    );
  };

  const updateSectionName: (
    id: string,
    suuid: string,
    newName: string
  ) => void = (id, suuid, newName) => {
    setIngredientSections(
      id,
      recipes
        .find((r) => r.uuid == id)
        ?.ingredientSections.map((section) => {
          if (section.uuid != suuid) return section;

          return {
            ...section,
            name: newName,
          };
        }) ?? []
    );
  };

  const addSectionIngredient: (
    id: string,
    suuid: string,
    newIngredient: PartialRecipe["ingredientSections"][number]["ingredients"][number]
  ) => void = (id, suuid, newIngredient) => {
    setIngredientSections(
      id,
      recipes
        .find((r) => r.uuid == id)
        ?.ingredientSections.map((section) => {
          if (section.uuid != suuid) return section;

          return {
            ...section,
            ingredients: [...section.ingredients, newIngredient],
          };
        }) ?? []
    );
  };

  const removeSection: (id: string, suuid: string) => void = (id, suuid) => {
    setIngredientSections(
      id,
      recipes
        .find((r) => r.uuid == id)
        ?.ingredientSections.filter((section) => section.uuid != suuid) ?? []
    );
  };

  const setIngredient: (
    id: string,
    suuid: string, // section uuid
    sub: string,
    newSelected?: PartialRecipe["ingredientSections"][number]["ingredients"][number]["selected"]
  ) => void = (id, suuid, sub, newSelected) => {
    setRecipes((recipes) =>
      recipes.map((r) => {
        if (r.uuid != id) return r;

        return {
          ...r,
          ingredientSections: r.ingredientSections.map((section) => {
            if (section.uuid != suuid) return section;

            return {
              ...section,
              ingredients: section.ingredients.map((ingredient) => {
                if (ingredient.uuid != sub) return ingredient;

                const tempIngredient = defaultIngredients.find(
                  (di) => di.id == newSelected?.id
                );
                if (!tempIngredient) return ingredient;

                return {
                  ...ingredient,
                  selected: newSelected,
                  unitId: newSelected
                    ? searchForUnit(
                        combineUnits(tempIngredient),
                        ingredient.derivedUnit,
                        tempIngredient.density ?? undefined,
                        tempIngredient.selectionUnit ?? undefined
                      )?.id
                    : undefined,
                };
              }),
            };
          }),
        };
      })
    );
  };

  const setBaseValue: <K extends keyof PartialRecipe>(
    key: K
  ) => (id: string, value: PartialRecipe[K]) => void = (key) => (id, value) => {
    setRecipes((recipes) =>
      recipes.map((r, i) => {
        if (r.uuid != id) return r;

        return {
          ...r,
          [key]: value,
        };
      })
    );
  };
  const setName = setBaseValue("name");
  const setPublic = setBaseValue("public");
  const setServings = setBaseValue("servings");
  const setMadeInGroups = setBaseValue("madeInGroups");
  const setClassId = setBaseValue("classId");
  const setTags = setBaseValue("tags");
  const setFolder = setBaseValue("folder");
  const setMethod = setBaseValue("method");
  const setImage = setBaseValue("image");
  const setInformation = setBaseValue("recipeInformation");

  const setValue: <
    K extends keyof PartialRecipe["ingredientSections"][number]["ingredients"][number]
  >(
    key: K
  ) => (
    id: string,
    suuid: string, // section uuid
    sub: string,
    value: PartialRecipe["ingredientSections"][number]["ingredients"][number][K]
  ) => void = (key) => (id, suuid, sub, value) => {
    setRecipes((recipes) =>
      recipes.map((r, i) => {
        if (r.uuid != id) return r;

        return {
          ...r,
          ingredientSections: r.ingredientSections.map((section) => {
            if (section.uuid != suuid) return section;

            return {
              ...section,
              ingredients: section.ingredients.map((ingredient) => {
                if (ingredient.uuid != sub) return ingredient;

                return {
                  ...ingredient,
                  [key]: value,
                };
              }),
            };
          }),
        };
      })
    );
  };
  const setUnitId = setValue("unitId");
  const setAmount = setValue("amount");
  const setPreparation = setValue("preparation");

  const removeRecipe = (id: string) => {
    setRecipes((recipes) => recipes.filter((r) => r.uuid !== id));
  };

  const removeIngredient = (id: string, suuid: string, sub: string) => {
    setRecipes((recipes) =>
      recipes.map((r) => {
        if (r.uuid != id) return r;

        return {
          ...r,
          ingredientSections: r.ingredientSections.map((section) => {
            if (section.uuid != suuid) return section;

            return {
              ...section,
              ingredients: section.ingredients.filter(
                (ingredient) => ingredient.uuid !== sub
              ),
            };
          }),
        };
      })
    );
  };

  // For a faster UI response, we use temp variables
  // and debounce the actual update
  const [tempName, setTempName] = useState(name || "");
  const [tempPublic, setTempPublic] = useState(isPublic);
  const [tempServings, setTempServings] = useState(servings || 1);
  const [tempMadeInGroups, setTempMadeInGroups] = useState(madeInGroups);
  const [tempTags, setTempTags] = useState(ingredientTags);
  const [tempFolder, setTempFolder] = useState(folder);
  const [tempClassId, setTempClassId] = useState(classId);
  const recipeClass: ShapedClass | undefined = tempClassId
    ? user.school?.classes
        .map(shapeClassLinear)
        .find((c) => c.obj.id == tempClassId)
    : undefined;
  const [tempMethod, setTempMethod] = useState(method || "");
  const [tempImage, setTempImage] = useState(image);
  const [tempInformation, setTempInformation] = useState(recipeInformation);

  const debouncedName = useDebounce(tempName, 5000);
  const debouncedPublic = useDebounce(tempPublic, 5000);
  const debouncedServings = useDebounce(tempServings, 5000);
  const debouncedMadeInGroups = useDebounce(tempMadeInGroups, 5000);
  const debouncedTags = useDebounce(tempTags, 5000);
  const debouncedFolder = useDebounce(tempFolder, 5000);
  const debouncedClassId = useDebounce(tempClassId, 5000);
  const debouncedMethod = useDebounce(tempMethod, 5000);
  const debouncedImage = useDebounce(tempImage, 5000);
  const debouncedInformation = useDebounce(tempInformation, 5000);

  useEffect(() => {
    if (name !== debouncedName) {
      console.log("name changed");
      setName(uuid, debouncedName);
    }
  }, [debouncedName]);
  useEffect(() => {
    if (isPublic !== debouncedPublic) {
      console.log("public changed");
      setPublic(uuid, debouncedPublic);
    }
  }, [debouncedPublic]);
  useEffect(() => {
    if (servings !== debouncedServings) {
      console.log("servings changed");
      setServings(uuid, debouncedServings);
    }
  }, [debouncedServings]);
  useEffect(() => {
    if (madeInGroups !== debouncedMadeInGroups) {
      console.log("madeInGroups changed");
      setMadeInGroups(uuid, debouncedMadeInGroups);
    }
  }, [debouncedMadeInGroups]);
  useEffect(() => {
    if (ingredientTags !== debouncedTags) {
      console.log("tags changed");
      setTags(uuid, debouncedTags);
    }
  }, [debouncedTags]);
  useEffect(() => {
    if (folder !== debouncedFolder) {
      console.log("folder changed");
      setFolder(uuid, debouncedFolder);
    }
  }, [debouncedFolder]);
  useEffect(() => {
    if (classId !== debouncedClassId) {
      console.log("class id changed");
      setClassId(uuid, debouncedClassId);
    }
  }, [debouncedClassId]);
  useEffect(() => {
    if (method !== debouncedMethod) {
      console.log("method changed");
      setMethod(uuid, debouncedMethod);
    }
  }, [debouncedMethod]);
  useEffect(() => {
    if (image !== debouncedImage) {
      console.log("image changed");
      setImage(uuid, debouncedImage);
    }
  }, [debouncedImage]);
  useEffect(() => {
    if (recipeInformation !== debouncedInformation) {
      console.log("information changed");
      setInformation(uuid, debouncedInformation);
    }
  }, [debouncedInformation]);

  const unsortedClasses =
    isAdmin(user) || isAssistant(user)
      ? user.school?.classes
      : user.school?.classes.filter((c) =>
          c.users.some((u) => u.id === user.id)
        );

  const folders = user.school?.recipeCollections ?? [];

  return (
    <div className="group relative rounded-lg border border-solid border-gray-300 bg-gray-50 p-2.5 shadow transition hover:shadow-lg">
      <button
        className="absolute -right-3.5 -top-3.5 rounded-full border border-solid border-red-100 bg-red-50 p-0.5 opacity-0 transition hover:bg-red-100 group-hover:opacity-100"
        onClick={() => removeRecipe(uuid)}
      >
        <TrashIcon className="m-1.5 w-6 text-red-600 hover:text-red-800" />
      </button>
      <div className="flex items-center gap-2.5">
        <input
          type="text"
          data-id={uuid}
          value={tempName}
          className="input flex-1 p-2 text-xl font-normal text-gray-600"
          onChange={(e) => {
            setTempName(e.target.value);
          }}
        />
      </div>
      <div className="mt-2">
        <SimilarRecipes schoolID={user.school?.id || ""} query={tempName} />
      </div>
      {source ? (
        <div>
          <h4>Source</h4>
          <a
            href={source}
            target="_blank"
            rel="noreferrer"
            className="text-blue-500 hover:text-blue-700"
          >
            {source}
          </a>

          <div className="mt-2 w-max max-w-2xl rounded-md bg-blue-50 p-2.5">
            <div className="flex">
              <div className="flex-shrink-0">
                <QuestionMarkCircleIcon
                  className="w-5 text-blue-700"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-2 flex-1 md:flex md:justify-between">
                <p className="text-lg font-normal text-blue-700">
                  Incorrect results from fetch?{" "}
                  <button
                    className="visible inline font-medium text-blue-900 hover:text-black"
                    onClick={() => {
                      (window as any).Intercom(
                        "showNewMessage",
                        `Hi, I'm getting incorrect results when fetching recipes from ${source}`
                      );
                    }}
                  >
                    Let us know.
                  </button>
                </p>
                {/* <p className="text-xdl mt-3 md:mt-0 md:ml-3.5">
                  <a
                    href="#"
                    className="whitespace-nowrap font-medium text-blue-700 hover:text-blue-600"
                  >
                    Details <span aria-hidden="true">&rarr;</span>
                  </a>
                </p> */}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-5">
        <div className="flex-1 space-y-5">
          <div className="my-5">
            <div className="flex items-center justify-between">
              <label htmlFor="servings" className="flex flex-col items-center">
                <span>Servings</span>
                <input
                  type="number"
                  min={1}
                  name="servings"
                  placeholder="How many servings is this for"
                  className="input w-16"
                  onChange={(e) => setTempServings(parseFloat(e.target.value))}
                  value={tempServings || ""}
                  required
                />
              </label>
              <div className="flex flex-col items-center justify-center">
                <span>Will it be made in groups? (optional)</span>
                <select
                  name="madeInGroups"
                  id="madeIngroups"
                  value={tempMadeInGroups || ""}
                  onChange={(e) => {
                    setTempMadeInGroups(
                      (e.target.value as Recipe_MadeInGroups) || null
                    );
                  }}
                  className="ml-1.5 rounded border border-solid border-gray-300 p-1.5"
                >
                  <option value="">(select an option)</option>
                  <option value={Recipe_MadeInGroups.Individuals}>
                    individually
                  </option>
                  <option value={Recipe_MadeInGroups.Pairs}>in pairs</option>
                  <option value={Recipe_MadeInGroups.Trios}>
                    in groups of three
                  </option>
                  <option value={Recipe_MadeInGroups.Four}>
                    in groups of four
                  </option>
                  <option value={Recipe_MadeInGroups.Five}>
                    in groups of five
                  </option>
                  <option value={Recipe_MadeInGroups.Six}>
                    in groups of six
                  </option>
                </select>
              </div>
            </div>
            {tempMadeInGroups ? (
              <small className="mt-1 block max-w-md rounded border border-solid border-blue-200 bg-blue-50 p-1.5 font-normal text-blue-600">
                Note: This does not affect orders, it is only for informational
                purposes. If you want to change how much this recipe produces,
                update the servings or the ingredients themselves.
              </small>
            ) : null}
          </div>

          <label htmlFor="recipe-class">
            <label>Class (Optional)</label>
            <AsyncSelect
              id="recipe-class"
              className="radio-select"
              classNamePrefix="react-select"
              isClearable={true}
              isSearchable={true}
              value={recipeClass}
              defaultOptions
              loadOptions={(inputValue, callback) => {
                callback(
                  searchClasses(
                    inputValue,
                    unsortedClasses?.map(shapeClass) ?? []
                  )
                );
              }}
              noOptionsMessage={() => "No Options"}
              maxMenuHeight={200}
              placeholder="Select a Class to Connect"
              onChange={(data) => {
                if (data?.value) {
                  setTempClassId(data.obj.id);
                } else {
                  setTempClassId(undefined);
                }
              }}
              // onChange={(newValue) => setNewClass(newValue || null)}
              styles={{
                container: (provided) => ({
                  ...provided,
                  position: "sticky",
                  top: "0px",
                  marginTop: "0",
                  zIndex: 20,
                }),
                control: (styles) => ({
                  ...styles,
                  backgroundColor: "white",
                }),
                option: (styles, { data }) => ({
                  ...styles,
                  ...dot(...data.colors),
                }),
                input: (styles) => ({
                  ...styles,
                  ...dot(...(recipeClass?.colors || [])),
                }),
                placeholder: (styles) => ({ ...styles, ...dot() }),
                singleValue: (provided, { data }) => ({
                  ...provided,
                  ...dot(...data.colors),
                }),
              }}
            />
          </label>

          <label htmlFor="tags">
            Tags
            {user.school?.requiresTaggedRecipes ? "" : " (optional)"}
            <UserContext.Consumer>
              {(user) => (
                <AncillariesComponent
                  variables={{ schoolID: user.school?.id || "" }}
                >
                  {({ error, fetching, data }) =>
                    error ? (
                      <ErrorMessage error={error} />
                    ) : fetching ? (
                      <div className="spinner" />
                    ) : !isStudent(user) || user.school?.permCreateTags ? (
                      <AsyncCreatableSelect
                        id="tags"
                        placeholder="Search or Create"
                        isClearable
                        isMulti
                        isSearchable
                        defaultOptions
                        noOptionsMessage={() => "No Options"}
                        value={tempTags}
                        loadOptions={(inputValue, callback) => {
                          callback(searchTags(inputValue, data?.tags ?? []));
                        }}
                        onCreateOption={(inputValue) => {
                          const newTag = {
                            value: cuid(),
                            label: inputValue,
                            __isNew__: true,
                          };
                          setTempTags([...tempTags, newTag]);
                        }}
                        onChange={(value) => setTempTags(value || [])}
                        maxMenuHeight={250}
                        // styles={{
                        //   container: (provided) => ({
                        //     ...provided,
                        //     width: "300px",
                        //   }),
                        // }}
                      />
                    ) : (
                      <AsyncSelect
                        id="tags"
                        placeholder="Search..."
                        isClearable
                        isMulti
                        isSearchable
                        defaultOptions
                        value={tempTags}
                        loadOptions={(inputValue, callback) => {
                          callback(searchTags(inputValue, data?.tags ?? []));
                        }}
                        noOptionsMessage={() => "No Options"}
                        onChange={(value) => setTempTags(value || [])}
                        maxMenuHeight={250}
                        // styles={{
                        //   container: (provided) => ({
                        //     ...provided,
                        //     width: "300px",
                        //   }),
                        // }}
                      />
                    )
                  }
                </AncillariesComponent>
              )}
            </UserContext.Consumer>
          </label>

          <label htmlFor="folder" className="block">
            Folder
            <FolderSelectInput
              sortedFolders={folders}
              showAll={true}
              //
              // style={{ width: "400px" }}
              id="folder"
              className="block w-full"
              value={tempFolder}
              onChange={(e) => setTempFolder(e.target.value)}
            />
          </label>

          {isTeacher(user) ? (
            <label
              htmlFor={"publicRecipe" + uuid}
              className="flex items-center"
            >
              <input
                type="checkbox"
                name={"publicRecipe" + uuid}
                id={"publicRecipe" + uuid}
                checked={tempPublic}
                onChange={(event) => {
                  setTempPublic(event.target.checked);
                }}
                className="m-2"
                style={{ display: "inline" }}
              />
              Shared
              <SharedRecipeInfo />
            </label>
          ) : null}
        </div>

        <div className="my-5">
          {tempImage ? (
            <div className="relative mx-auto w-max">
              <img
                src={tempImage}
                alt={`Image of ${tempName}`}
                className="mx-auto my-5 block rounded-lg"
                style={{
                  maxWidth: "300px",
                  maxHeight: "300px",
                  minWidth: "150px",
                  minHeight: "150px",
                  boxShadow: "#a0a0a0 0px 0px 5px",
                }}
              />
              <Tooltip content="Replace Image">
                <button
                  className="btn absolute bottom-0 left-0"
                  onClick={() => setVisibleDialog(uuid)}
                >
                  <ArrowPathIcon className="w-6" />
                </button>
              </Tooltip>
              <Tooltip content="Remove Image">
                <button
                  className="btn absolute bottom-0 right-0"
                  onClick={() => setTempImage("")}
                >
                  <TrashIcon className="w-6 text-red-600" />
                </button>
              </Tooltip>
            </div>
          ) : (
            <div className="relative mx-auto w-max rounded-lg border border-solid border-gray-400 bg-gradient-to-tr from-cyan-100 to-green-100 px-14 py-14 text-center">
              <CameraIcon className="w-18 text-gray-400" />
              <p>No Image</p>
              <Tooltip content="Add an Image">
                <button
                  className="btn btn-primary absolute bottom-0 right-0"
                  onClick={() => setVisibleDialog(uuid)}
                >
                  <PlusIcon className="w-6" />
                </button>
              </Tooltip>
            </div>
          )}
          <DialogComponent
            isOpen={visibleDialog == uuid}
            onClose={() => setVisibleDialog(null)}
            aria-label="work in progress"
          >
            <GetImageURL
              onCompleted={(url) => {
                setVisibleDialog(null);

                setTempImage(url);
              }}
            />
            <button className="btn" onClick={() => setVisibleDialog(null)}>
              Close
            </button>
          </DialogComponent>
        </div>
      </div>
      <div>
        <h4>Method</h4>
        <TextEditor
          // content={tempMethod?.replace(/\n/g, "<br /></br />")}
          // I forgot why the break tags were there
          content={tempMethod}
          onUpdate={(content: string) => setTempMethod(content)}
          deps={[
            tempName,
            tempPublic,
            tempImage,
            tempServings,
            tempMadeInGroups,
            tempClassId,
            tempTags,
            ingredientSections,
            tempInformation,
          ]}
        />
      </div>
      {tempInformation && tempInformation.length ? (
        <div>
          <h4>Extra Information</h4>
          {tempInformation.map((contents, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Input
                type="text"
                style={{ width: "300px" }}
                value={contents}
                onChange={(e) =>
                  setTempInformation(
                    tempInformation.map((c, i) =>
                      i != index ? c : e.target.value
                    )
                  )
                }
              />
              <button
                className=""
                onClick={() =>
                  setTempInformation(
                    tempInformation.filter((c, i) => i != index)
                  )
                }
              >
                <TrashIcon className="m-1.5 w-6 text-red-600 hover:text-red-800" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <DragNDrop
        collection={ingredientSections}
        itemKey="ingredients"
        setCollection={(newSections) =>
          setIngredientSections(uuid, newSections)
        }
      >
        {(ingredientSections) => (
          <div className="mt-16">
            <h4>
              Ingredients (
              {ingredientSections.reduce(
                (acc, s) => acc + s.ingredients.length,
                0
              )}
              )
            </h4>
            <div className="space-y-5">
              {ingredientSections.map((ris, risIndex) => (
                <IngredientSection
                  key={ris.uuid}
                  uuid={uuid}
                  ris={ris}
                  risIndex={risIndex}
                  updateSectionName={updateSectionName}
                  addSectionIngredient={addSectionIngredient}
                  removeSection={removeSection}
                  setIngredient={setIngredient}
                  setAmount={setAmount}
                  setPreparation={setPreparation}
                  setUnitId={setUnitId}
                  removeIngredient={removeIngredient}
                />
              ))}
            </div>
          </div>
        )}
      </DragNDrop>
      <div className="flex items-center justify-center">
        <button
          className="btn mt-6 text-lg font-medium"
          onClick={() => {
            const newSection = {
              uuid: cuid(),
              name: "",
              section: true,
              ingredients: [],
            };

            setIngredientSections(uuid, [...ingredientSections, newSection]);
          }}
        >
          <PlusIcon className="mr-2 w-6" /> Add an Ingredient Section
        </button>
      </div>
    </div>
  );
};
